import { useEffect, useState } from 'react';
import { ButtonWrapper, IconsTabWrapper, TabButton, TabLineButton, TabsWrapper } from '../../styles/button.style';
import { TabsPanelHeader } from '../../styles/panel.style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCargoTempLogStore } from '../../context/CargoTempLogContext';
import { useVesselStore } from '../../context/VesselContext';
import useCargoTemperatureApi from '../../api/cargo-temp-log/useCargoTempLogApi';
import { tabEnum, validateCargoTempLog } from './CargoTemperatureLogUtils';
import { CargoTempLogTabText } from './CargoTempLog.style';
import { CargoTemperatureLogDate } from '../../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import CargoTempLogStatusIcon from '../ui/status-icon/CargoTempLogStatusIcon';
import { CargoTempLogStatusEnum } from '../../enums/cargoTempLogEnum';
import { CargoTemperatureConfig } from '../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { mapCargoTempLogDateBm } from '../../factory/CargoTempLog/CargoTempLogDateFactory';
import { isDateSame, isDateSameOrAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { RoleTypeEnum } from '../../enums/roleTypeEnum';
import { useAuthStore } from '../../context/AuthContext';
import UpdateInfo from '../common/UpdateInfo';
import { isDateAfter } from 'odfjell-date-time-utils/dateTimeUtils';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { isInhibitorQuestionnaireCompleted } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { CargoTempCategory } from 'src/types/ViewModel/CargoTempLog/CargoInTankItem';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useAppStore } from 'src/context/AppContext';

type Props = {
    populatedCargoTempLogDates: CargoTemperatureLogDate[];
    cargoTemperatureConfigs: CargoTemperatureConfig[];
};

const CargoTempLogHeaderComponent: React.FC<Props> = (props: Props) => {
    const { vessel } = useVesselStore();
    const {
        setIsCommentsVisible,
        isCommentsVisible,
        filteredCargoTemperatureConfigs,
        cargoTempLogReportLog,
        selectedCargoTempDate,
        selectedTab,
        setSelectedTab,
    } = useCargoTempLogStore();
    const { saveCargoTemperatureConfigs, saveCargoTemperatureLog } = useCargoTemperatureApi();
    const [hasDayCargo, setHasDayCargo] = useState<boolean | null>(null);
    const [allCargoConfigured, setAllCargoConfigured] = useState<boolean | null>(null);
    const [cargoTempLogDate, setCargoTempLogDate] = useState<CargoTemperatureLogDate | null>(null);
    const { role } = useAuthStore();
    const { isOffline } = useConnectionStore();

    const { appFeatureSettings } = useAppStore();
    const [inhibitorQuestionnaireEnabled, setInhibitorQuestionnaireEnabled] = useState<boolean>(false);

    useEffect(() => {
        if (appFeatureSettings) {
            const cargoTempLogQuestionnaire = appFeatureSettings.find((x) => x.appFeatureId === AppFeatureEnum.CargoTempLogQuestionnaire);
            if (cargoTempLogQuestionnaire && isDateSameOrAfter(selectedCargoTempDate, cargoTempLogQuestionnaire.fromDate)) {
                setInhibitorQuestionnaireEnabled(true);
            } else {
                setInhibitorQuestionnaireEnabled(false);
            }
        }
    }, [appFeatureSettings]);

    useEffect(() => {
        if (selectedTab === tabEnum.export) setSelectedTab(tabEnum.reporting);

        if (
            props.populatedCargoTempLogDates &&
            props.populatedCargoTempLogDates.length > 0 &&
            selectedCargoTempDate &&
            props.cargoTemperatureConfigs
        ) {
            const _cargoTempLogDate = props.populatedCargoTempLogDates.find((d) => isDateSame(d.logDate, selectedCargoTempDate));
            const _cargo = _cargoTempLogDate?.cargoInTankList?.map((c) => c.cargoNumber);

            const _allCargoConfigured =
                _cargo &&
                _cargo.length > 0 &&
                _cargo.every((cargoNumber) => {
                    const config = props.cargoTemperatureConfigs?.find((conf) => conf.cargoNumber === cargoNumber);
                    if (!config) return false;

                    // Check if this cargo is inhibited by looking at cargoInTankList
                    const cargoInTank = _cargoTempLogDate?.cargoInTankList.find((c) => c.cargoNumber === cargoNumber);
                    const isInhibited = cargoInTank?.cargoTempCategory === CargoTempCategory.Inhibited;

                    // If cargo is inhibited, also check that questionnaire is completed
                    if (inhibitorQuestionnaireEnabled && isInhibited && !isInhibitorQuestionnaireCompleted(config)) {
                        return false;
                    }

                    return true;
                });
            setAllCargoConfigured(_allCargoConfigured);

            if (!_cargo || _cargo.length === 0) {
                setHasDayCargo(false);
            } else {
                setHasDayCargo(true);
            }

            setCargoTempLogDate(_cargoTempLogDate);
        }
    }, [props.populatedCargoTempLogDates, props.cargoTemperatureConfigs, selectedCargoTempDate]);

    useEffect(() => {
        if (selectedTab === tabEnum.export) setSelectedTab(tabEnum.reporting);
    }, [selectedCargoTempDate]);

    const onShowComments = () => {
        setIsCommentsVisible(!isCommentsVisible);
    };

    const onSaveReport = () => {
        if (cargoTempLogReportLog) {
            const cargoTempLogDate = props.populatedCargoTempLogDates.find((d) => isDateSame(d.logDate, selectedCargoTempDate));
            if (!cargoTempLogDate) return;
            cargoTempLogDate.cargoTemperatureLog = cargoTempLogReportLog;
            const validation = validateCargoTempLog(cargoTempLogDate, cargoTempLogReportLog, props.cargoTemperatureConfigs);
            cargoTempLogDate.isComplete = validation;
            const bm = mapCargoTempLogDateBm(cargoTempLogDate);
            saveCargoTemperatureLog(vessel.imoNumber, bm);
        }
    };

    const onSaveConfig = () => {
        if (!vessel || !filteredCargoTemperatureConfigs) return;
        saveCargoTemperatureConfigs(vessel.imoNumber, filteredCargoTemperatureConfigs);
    };

    const GetReportingStatusIconElement = () => {
        if (allCargoConfigured && cargoTempLogDate) {
            return CargoTempLogStatusIcon(cargoTempLogDate?.isComplete ? CargoTempLogStatusEnum.complete : CargoTempLogStatusEnum.incomplete);
        }
    };

    const GetConfigStatusIconElement = () => {
        if (!allCargoConfigured && hasDayCargo && cargoTempLogDate) {
            return CargoTempLogStatusIcon(CargoTempLogStatusEnum.configMissing);
        }
    };

    const GetSaveButtonText = () => {
        if (cargoTempLogDate.isComplete) return 'Update';
        else if (cargoTempLogDate.isValidated) return 'Complete';
        else return 'Save';
    };

    const getLatestUpdatedItem = (cargoTemperatureConfigs: CargoTemperatureConfig[]): CargoTemperatureConfig | null => {
        if (cargoTemperatureConfigs && cargoTemperatureConfigs.length > 0) {
            let latestCargoTempConfig: CargoTemperatureConfig = cargoTemperatureConfigs[0];

            let latestUpdatedUtc = new Date('1970-01-01');

            cargoTemperatureConfigs.forEach((c) => {
                if (c.lastUpdatedUtc && isDateAfter(c.lastUpdatedUtc, latestUpdatedUtc)) {
                    latestUpdatedUtc = new Date(c.lastUpdatedUtc);
                    latestCargoTempConfig = c;
                }
            });

            return latestCargoTempConfig;
        }

        return null;
    };

    return (
        <TabsPanelHeader columns={'60% 32% 7%'}>
            <TabsWrapper tabs={'125px 125px'}>
                <TabButton className="first" isSelected={selectedTab === tabEnum.reporting} onClick={() => setSelectedTab(tabEnum.reporting)}>
                    <CargoTempLogTabText>Reporting</CargoTempLogTabText>
                    <IconsTabWrapper>{GetReportingStatusIconElement()}</IconsTabWrapper>
                </TabButton>
                {!isOffline && (
                    <TabButton className="last" isSelected={selectedTab === tabEnum.export} onClick={() => setSelectedTab(tabEnum.export)}>
                        <CargoTempLogTabText>Export</CargoTempLogTabText>
                        <IconsTabWrapper></IconsTabWrapper>
                    </TabButton>
                )}
                <TabButton className="seperate-from-group" isSelected={selectedTab === tabEnum.config} onClick={() => setSelectedTab(tabEnum.config)}>
                    <CargoTempLogTabText>Configuration</CargoTempLogTabText>
                    <IconsTabWrapper>{GetConfigStatusIconElement()}</IconsTabWrapper>
                </TabButton>
            </TabsWrapper>
            <div>
                {selectedTab === tabEnum.config && getLatestUpdatedItem(props.cargoTemperatureConfigs)?.lastUpdatedUtc && (
                    <UpdateInfo
                        lastUpdatedUtc={getLatestUpdatedItem(props.cargoTemperatureConfigs).lastUpdatedUtc}
                        lastUpdatedBy={getLatestUpdatedItem(props.cargoTemperatureConfigs).lastUpdatedBy}
                    ></UpdateInfo>
                )}
                {selectedTab === tabEnum.reporting && cargoTempLogDate?.lastUpdatedUtc && (
                    <UpdateInfo lastUpdatedUtc={cargoTempLogDate.lastUpdatedUtc} lastUpdatedBy={cargoTempLogDate.lastUpdatedBy}></UpdateInfo>
                )}
            </div>
            {hasDayCargo && (
                <>
                    {selectedTab === tabEnum.reporting && (
                        <ButtonWrapper>
                            <TabLineButton type="button" isSelected={isCommentsVisible} onClick={() => onShowComments()}>
                                <FontAwesomeIcon title="Toggle showing comments" icon="comment" />
                            </TabLineButton>
                            {role &&
                                (role.roleType === RoleTypeEnum.Admin ||
                                    role.roleType === RoleTypeEnum.ShipUser ||
                                    role.roleType === RoleTypeEnum.FleetPerformanceAnalyst) && (
                                    <TabLineButton type="button" onClick={() => onSaveReport()}>
                                        {GetSaveButtonText()}
                                    </TabLineButton>
                                )}
                        </ButtonWrapper>
                    )}
                    {selectedTab === tabEnum.config &&
                        role &&
                        (role.roleType === RoleTypeEnum.Admin ||
                            role.roleType === RoleTypeEnum.ShipUser ||
                            role.roleType === RoleTypeEnum.FleetPerformanceAnalyst) && (
                            <ButtonWrapper>
                                <TabLineButton onClick={onSaveConfig}>Save</TabLineButton>
                            </ButtonWrapper>
                        )}
                </>
            )}
        </TabsPanelHeader>
    );
};

export default CargoTempLogHeaderComponent;
